// Curriculum skill taxonomy — a pure data + helper leaf (no engine/render
// imports; mirrors coach-params.ts). Ordered roughly by how soon a skill pays
// off in real music. `prereqs` is the DAG the sequencer walks;
// introGrade..bandMax (RCM grades) is the band a skill's drills/pieces occupy.

import type { SkillId } from "../types";

export type SkillGroup = "keys" | "patterns" | "shapes" | "progressions" | "read" | "rhythm";

export interface Skill {
  id: SkillId;
  group: SkillGroup;
  label: string;
  prereqs: SkillId[];
  introGrade: number;
  bandMax: number;
}

export const SKILLS: readonly Skill[] = [
  { id: "key-c", group: "keys", label: "Key of C", prereqs: [], introGrade: 1, bandMax: 1 },
  {
    id: "five-finger",
    group: "patterns",
    label: "Five-finger position",
    prereqs: [],
    introGrade: 1,
    bandMax: 1,
  },
  {
    id: "blocked-chords",
    group: "patterns",
    label: "Blocked chords",
    prereqs: ["five-finger"],
    introGrade: 1,
    bandMax: 3,
  },
  {
    id: "triad-major",
    group: "shapes",
    label: "Major triad",
    prereqs: ["blocked-chords"],
    introGrade: 1,
    bandMax: 3,
  },
  {
    id: "dom7",
    group: "shapes",
    label: "Dominant 7th",
    prereqs: ["triad-major"],
    introGrade: 1,
    bandMax: 5,
  },
  { id: "key-g", group: "keys", label: "Key of G", prereqs: ["key-c"], introGrade: 1, bandMax: 2 },
  { id: "key-f", group: "keys", label: "Key of F", prereqs: ["key-c"], introGrade: 1, bandMax: 2 },
  {
    id: "prog-I-IV-V",
    group: "progressions",
    label: "I–IV–V",
    prereqs: ["triad-major", "key-c"],
    introGrade: 1,
    bandMax: 4,
  },
  {
    id: "hand-independence",
    group: "patterns",
    label: "Hand independence",
    prereqs: ["five-finger"],
    introGrade: 2,
    bandMax: 5,
  },
  {
    id: "broken-chords",
    group: "patterns",
    label: "Broken chords",
    prereqs: ["blocked-chords"],
    introGrade: 2,
    bandMax: 4,
  },
  {
    id: "scale-major",
    group: "patterns",
    label: "Major scale",
    prereqs: ["five-finger", "key-c"],
    introGrade: 2,
    bandMax: 4,
  },
  {
    id: "triad-minor",
    group: "shapes",
    label: "Minor triad",
    prereqs: ["triad-major"],
    introGrade: 2,
    bandMax: 4,
  },
  {
    id: "inversion-1",
    group: "shapes",
    label: "First inversion",
    prereqs: ["triad-major"],
    introGrade: 2,
    bandMax: 4,
  },
  { id: "key-d", group: "keys", label: "Key of D", prereqs: ["key-g"], introGrade: 2, bandMax: 3 },
  {
    id: "key-a-minor",
    group: "keys",
    label: "Key of A minor",
    prereqs: ["key-c"],
    introGrade: 2,
    bandMax: 3,
  },
  {
    id: "prog-I-V-vi-IV",
    group: "progressions",
    label: "I–V–vi–IV",
    prereqs: ["prog-I-IV-V", "triad-minor"],
    introGrade: 2,
    bandMax: 5,
  },
  {
    id: "arpeggios",
    group: "patterns",
    label: "Arpeggios",
    prereqs: ["broken-chords"],
    introGrade: 3,
    bandMax: 5,
  },
  {
    id: "alberti-bass",
    group: "patterns",
    label: "Alberti bass",
    prereqs: ["broken-chords"],
    introGrade: 3,
    bandMax: 5,
  },
  {
    id: "scale-minor",
    group: "patterns",
    label: "Minor scale",
    prereqs: ["scale-major", "key-a-minor"],
    introGrade: 3,
    bandMax: 5,
  },
  {
    id: "inversion-2",
    group: "shapes",
    label: "Second inversion",
    prereqs: ["inversion-1"],
    introGrade: 3,
    bandMax: 5,
  },
  {
    id: "prog-ii-V-I",
    group: "progressions",
    label: "ii–V–I",
    prereqs: ["dom7", "prog-I-IV-V"],
    introGrade: 3,
    bandMax: 6,
  },
  {
    id: "prog-12-bar-blues",
    group: "progressions",
    label: "12-bar blues",
    prereqs: ["dom7", "prog-I-IV-V"],
    introGrade: 3,
    bandMax: 6,
  },
  // Reading branch — an optional, parallel track (no technique/key/chord skill
  // depends on these). Reading is the `correctness` focus practised with the
  // falling-note highway faded; see docs/superpowers/specs/…-reading-tutor-design.md.
  {
    id: "read-landmarks",
    group: "read",
    label: "Read landmark notes",
    prereqs: [],
    introGrade: 1,
    bandMax: 1,
  },
  {
    id: "read-steps",
    group: "read",
    label: "Read stepwise motion",
    prereqs: ["read-landmarks"],
    introGrade: 1,
    bandMax: 2,
  },
  {
    id: "read-skips",
    group: "read",
    label: "Read skips (thirds)",
    prereqs: ["read-steps"],
    introGrade: 1,
    bandMax: 2,
  },
  // Rhythm branch — optional, parallel. Rhythm-as-execution: single-pitch drills
  // whose focus is `timing`; see docs/superpowers/specs/…-rhythm-drills-design.md.
  {
    id: "rhythm-eighths",
    group: "rhythm",
    label: "Even eighths",
    prereqs: [],
    introGrade: 1,
    bandMax: 2,
  },
  {
    id: "rhythm-dotted",
    group: "rhythm",
    label: "Dotted rhythm",
    prereqs: ["rhythm-eighths"],
    introGrade: 1,
    bandMax: 3,
  },
];

const ORDER = new Map(SKILLS.map((s, i) => [s.id, i]));
const BY_ID = new Map(SKILLS.map((s) => [s.id, s]));

/** Whether `x` is an id in the taxonomy. */
export function isSkillId(x: string): boolean {
  return BY_ID.has(x);
}

/** Index of a skill in the ordered list, or `Infinity` if unknown. */
export function skillOrder(id: SkillId): number {
  return ORDER.get(id) ?? Number.POSITIVE_INFINITY;
}

/** A skill's grade band, or null if unknown. */
export function skillBand(id: SkillId): { introGrade: number; bandMax: number } | null {
  const s = BY_ID.get(id);
  return s ? { introGrade: s.introGrade, bandMax: s.bandMax } : null;
}

/** RCM grade ceiling for *suggested* curriculum content (higher stays browsable). */
export const CURRICULUM_BAND_MAX = 4;

/** Human label for a skill id, or the id itself when unknown. */
export function skillLabel(id: SkillId): string {
  return BY_ID.get(id)?.label ?? id;
}

/**
 * Validate a skill list: unknown prereqs, bad bands, and cycles. Returns a list
 * of human-readable problems ([] = valid). Pure — used by the test gate and any
 * tooling that needs to vet a taxonomy.
 */
export function skillGraphErrors(skills: readonly Skill[]): string[] {
  const errors: string[] = [];
  const ids = new Set(skills.map((s) => s.id));
  for (const s of skills) {
    if (s.introGrade > s.bandMax)
      errors.push(`${s.id}: introGrade ${s.introGrade} > bandMax ${s.bandMax}`);
    for (const p of s.prereqs) {
      if (!ids.has(p)) errors.push(`${s.id}: unknown prereq "${p}"`);
    }
  }
  const color = new Map<string, 0 | 1 | 2>();
  const adj = new Map(skills.map((s) => [s.id, s.prereqs]));
  const visit = (id: string, stack: string[]): void => {
    if (color.get(id) === 2) return;
    if (color.get(id) === 1) {
      errors.push(`cycle: ${[...stack, id].join(" -> ")}`);
      return;
    }
    color.set(id, 1);
    for (const p of adj.get(id) ?? []) {
      if (ids.has(p)) visit(p, [...stack, id]);
    }
    color.set(id, 2);
  };
  for (const s of skills) visit(s.id, []);
  return errors;
}
